import { AvForm } from 'availity-reactstrap-validation';
import { useFormik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card, CardBody, CardHeader, Col, Collapse, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table } from 'reactstrap';
import ResultatForm from './ResultatForm';
import { vehiculeActions } from '../database/vehiculeSlice';
import { vvinaleActions } from './vvinaleSlice';

const initForm = {
    vehicule: null,
};

const evaluationRequiredFields = [
    { key: 'categVeh', label: 'catégorie du véhicule' },
    { key: 'puissFiscale', label: 'puissance fiscale', positive: true },
    { key: 'prixAchat', label: "prix d'achat", positive: true },
    { key: 'ageYears', label: 'âge en années' },
    { key: 'ageMonths', label: 'âge en mois' },
    { key: 'carburant', label: 'carburant' },
    { key: 'compteur', label: 'kilométrage' },
    { key: 'etatVehicule', label: 'état du véhicule' },
    { key: 'epave', label: 'valeur épave' },
    { key: 'devis', label: 'montant du devis' },
    { key: 'valMarche', label: 'valeur de marché' },
];

const missingEvaluationFields = (vehicle) => {
    if (!vehicle) return ['véhicule'];

    return evaluationRequiredFields
        .filter(({ key, positive }) => {
            const value = vehicle[key];
            if (value === null || value === undefined || value === '') return true;
            return positive && Number(value) <= 0;
        })
        .map(({ label }) => label);
};

const numericEvaluationFields = new Set([
    'puissFiscale',
    'prixAchat',
    'ageYears',
    'ageMonths',
    'compteur',
    'epave',
    'devis',
    'valMarche',
]);

const detailValue = (value, suffix = '') => {
    if (value === null || value === undefined || value === '') return '—';
    return `${value}${suffix}`;
};

const detailMoney = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '—';
    return `${amount.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} MAD`;
};

const VVinalForm = () => {
    const { t } = useTranslation('translation');
    const { categVH } = useSelector(state => state.data);
    const { isFetching, vehicules } = useSelector(state => state.vehicule);
    const { isFetchingV } = useSelector(state => state.vvinale);

    const [formData, setFormData] = useState(initForm);
    const [show, setShow] = useState(false);
    const [result, setResult] = useState(null);
    const [evaluationError, setEvaluationError] = useState('');
    const [errorModalTitle, setErrorModalTitle] = useState('Évaluation impossible');
    const [documentsExpanded, setDocumentsExpanded] = useState(true);
    const [quickEditOpen, setQuickEditOpen] = useState(false);
    const [quickEditVehicle, setQuickEditVehicle] = useState(null);
    const [quickEditError, setQuickEditError] = useState('');

    const dispatch = useDispatch();

    const formik = useFormik({
        initialValues: formData,
        enableReinitialize: true,
        onSubmit: (values) => {
            const missingFields = missingEvaluationFields(values.vehicule);
            if (missingFields.length) {
                setResult(null);
                setErrorModalTitle('Évaluation impossible');
                setEvaluationError(`Évaluation impossible. Complétez les données suivantes dans le dossier véhicule : ${missingFields.join(', ')}.`);
                return;
            }

            setEvaluationError('');
            let payload = {
                data: {
                    ...values.vehicule,
                    remise: values.vehicule.remise ?? 0,
                    fraisimm: values.vehicule.fraisimm ?? 0,
                },
                onSuccess: (data) => {
                    setEvaluationError('');
                    setResult(data);
                },
                onError: (message) => {
                    setResult(null);
                    setErrorModalTitle('Évaluation impossible');
                    setEvaluationError(message);
                },
            };

            dispatch(vvinaleActions.evaluate(payload));

            // if (values?.id > 0) {
            //     dispatch(parametrageActions.updateVvinale(payload));
            // } else {
            //     dispatch(parametrageActions.createVvinale(payload));
            // }
        }
    });

    useEffect(() => {
        load();
    }, []);

    const load = () => {
        let query = {
            criteria: {},
            page: 0,
            size: 5,
        };
        dispatch(vehiculeActions.search(query));
    }

    const { values } = formik;
    const selectedVehicleMissingFields = useMemo(
        () => missingEvaluationFields(values.vehicule),
        [values.vehicule]
    );

    const openQuickEdit = () => {
        if (!values.vehicule) return;
        setQuickEditVehicle({ ...values.vehicule });
        setQuickEditError('');
        setEvaluationError('');
        setQuickEditOpen(true);
    };

    const closeQuickEdit = () => {
        if (isFetching) return;
        setQuickEditOpen(false);
        setQuickEditError('');
    };

    const updateQuickEditField = (field, value) => {
        setQuickEditVehicle(current => ({ ...current, [field]: value }));
        setQuickEditError('');
    };

    const saveQuickVehicle = () => {
        const missingFields = missingEvaluationFields(quickEditVehicle);
        if (missingFields.length) {
            setQuickEditError(`Renseignez les champs suivants pour continuer : ${missingFields.join(', ')}.`);
            return;
        }

        const normalizedVehicle = {
            ...quickEditVehicle,
            ...Object.fromEntries(
                [...numericEvaluationFields].map(field => [field, Number(quickEditVehicle[field])])
            ),
            remise: quickEditVehicle.remise ?? 0,
            fraisimm: quickEditVehicle.fraisimm ?? 0,
        };

        dispatch(vehiculeActions.update({
            data: normalizedVehicle,
            onSuccess: () => {
                dispatch(vehiculeActions.find({
                    id: normalizedVehicle.id,
                    onSuccess: (updatedVehicle) => {
                        formik.setFieldValue('vehicule', { ...updatedVehicle });
                        setResult(null);
                        setQuickEditOpen(false);
                        setQuickEditError('');
                    },
                }));
            },
            onError: (error) => {
                setQuickEditError(
                    error?.response?.data?.message
                    || error?.response?.data?.body?.message
                    || error?.message
                    || "Le véhicule n'a pas pu être mis à jour."
                );
            },
        }));
    };

    const showVehiculePopup = () => {
        setShow(!show);
        document.body.classList.add("no_padding");
    }

    const handleVehicule = (checked, vh) => {
        setEvaluationError('');
        setResult(null);
        setDocumentsExpanded(true);
        if (!checked) {
            formik.setFieldValue('vehicule', null);
            return;
        }

        // Fetch FULL vehicule data (including documents)
        let payload = {
            id: vh.id,
            onSuccess: (fullVehicule) => {
                console.log(fullVehicule)
                formik.setFieldValue("vehicule", { ...fullVehicule });
            }
        };

        dispatch(vehiculeActions.find(payload));
    };

    const handleExport = () => {
        const missingFields = missingEvaluationFields(values.vehicule);
        if (missingFields.length) {
            setErrorModalTitle('Export impossible');
            setEvaluationError(`Le rapport ne peut pas être généré. Complétez les données suivantes dans le dossier véhicule : ${missingFields.join(', ')}.`);
            return;
        }

        setEvaluationError('');
        const payload = {
            data: {
                ...values.vehicule,
                remise: values.vehicule.remise ?? 0,
                fraisimm: values.vehicule.fraisimm ?? 0,
            },
            onSuccess: () => {
                setEvaluationError('');
            },
            onError: (message) => {
                setErrorModalTitle('Export impossible');
                setEvaluationError(message);
            },
        };

        dispatch(vvinaleActions.exportData(payload));

    }

    return (
        <React.Fragment>
            {isFetching || isFetchingV ? (
                <div id="preloader">
                    <div id="status">
                        <div className="spinner">
                            <i className="ri-loader-line spin-icon"></i>
                        </div>
                    </div>
                </div>
            ) : (
                <Row>
                    <Col md="8">
                        <AvForm className="needs-validation" onValidSubmit={formik.handleSubmit}>
                            <Card>
                                <CardBody>
                                    <Row>
                                        <Col md="6">
                                            <Button
                                                type="button"
                                                size='sm'
                                                onClick={showVehiculePopup}
                                                color="primary" className="waves-effect waves-light"
                                            >
                                                {t('parametrage.vehicule')}
                                            </Button>


                                        </Col>
                                    </Row>
                                    {formik.values.vehicule && (
                                        <Card className="mt-3 evaluation-vehicle-card">
                                            <div className="evaluation-vehicle-header">
                                                <div className="evaluation-vehicle-heading">
                                                    <span><i className="ri-car-line"></i></span>
                                                    <div>
                                                        <small>{t('parametrage.detailsVehicule')}</small>
                                                        <h5>{[values.vehicule.marque, values.vehicule.modele].filter(Boolean).join(' ') || 'Véhicule sélectionné'}</h5>
                                                        <p>{values.vehicule.immatriculation || values.vehicule.numChassis || `Dossier #${values.vehicule.id}`}</p>
                                                    </div>
                                                </div>
                                                <div className="evaluation-vehicle-header-actions">
                                                    <span className={`evaluation-vehicle-state ${values.vehicule.etatVehicule === 'ETAT_1' ? 'good' : values.vehicule.etatVehicule === 'ETAT_2' ? 'average' : 'missing'}`}>
                                                        <i className={values.vehicule.etatVehicule ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}></i>
                                                        {values.vehicule.etatVehicule === 'ETAT_1' ? 'Bon état' : values.vehicule.etatVehicule === 'ETAT_2' ? 'État moyen' : 'État non renseigné'}
                                                    </span>
                                                    <Button type="button" color="light" size="sm" className="evaluation-vehicle-edit" onClick={openQuickEdit}>
                                                        <i className="ri-pencil-line"></i>
                                                        Modifier
                                                    </Button>
                                                </div>
                                            </div>

                                            <CardBody>
                                                {selectedVehicleMissingFields.length > 0 && (
                                                    <div className="evaluation-missing-data">
                                                        <span className="evaluation-missing-data-icon">
                                                            <i className="ri-error-warning-line"></i>
                                                        </span>
                                                        <div>
                                                            <strong>{selectedVehicleMissingFields.length} donnée(s) requise(s) à compléter</strong>
                                                            <p>{selectedVehicleMissingFields.join(' · ')}</p>
                                                        </div>
                                                        <Button type="button" color="warning" size="sm" onClick={openQuickEdit}>
                                                            Compléter maintenant
                                                            <i className="ri-arrow-right-line"></i>
                                                        </Button>
                                                    </div>
                                                )}
                                                <div className="evaluation-vehicle-sections">
                                                    <section className="evaluation-detail-section">
                                                        <h6><i className="ri-fingerprint-line"></i>{t('vehiculef.section_chassis')}</h6>
                                                        <div className="evaluation-detail-grid">
                                                            <div><span>{t('vehiculef.immatriculation')}</span><strong>{detailValue(values.vehicule.immatriculation)}</strong></div>
                                                            <div><span>{t('vehiculef.immanterieur')}</span><strong>{detailValue(values.vehicule.immanterieur)}</strong></div>
                                                            <div className="wide"><span>{t('vehiculef.numChassis')}</span><strong>{detailValue(values.vehicule.numChassis)}</strong></div>
                                                            <div><span>{t('vehiculef.firstMec')}</span><strong>{detailValue(values.vehicule.firstMec)}</strong></div>
                                                            <div><span>{t('vehiculef.mecMaroc')}</span><strong>{detailValue(values.vehicule.mecMaroc)}</strong></div>
                                                            <div><span>{t('vehiculef.compteur')}</span><strong>{detailValue(values.vehicule.compteur, ' km')}</strong></div>
                                                            <div><span>Âge</span><strong>{detailValue(values.vehicule.ageYears, ' an(s)')} · {detailValue(values.vehicule.ageMonths, ' mois')}</strong></div>
                                                            <div><span>{t('vehiculef.carburant')}</span><strong>{detailValue(values.vehicule.carburant)}</strong></div>
                                                            <div><span>{t('vehiculef.puissFiscale')}</span><strong>{detailValue(values.vehicule.puissFiscale, ' CV')}</strong></div>
                                                            <div><span>{t('vehiculef.nbrCylindres')}</span><strong>{detailValue(values.vehicule.nbrCylindres)}</strong></div>
                                                            <div><span>Catégorie</span><strong>{detailValue(values.vehicule.categVeh)}</strong></div>
                                                        </div>
                                                    </section>

                                                    <section className="evaluation-detail-section">
                                                        <h6><i className="ri-shield-cross-line"></i>{t('vehiculef.section_sinistre')}</h6>
                                                        <div className="evaluation-detail-grid">
                                                            <div><span>{t('vehiculef.dateSinistre')}</span><strong>{detailValue(values.vehicule.dateSinistre)}</strong></div>
                                                            <div><span>{t('vehiculef.etatVehicule')}</span><strong>{values.vehicule.etatVehicule === 'ETAT_1' ? 'Bon' : values.vehicule.etatVehicule === 'ETAT_2' ? 'Moyen' : '—'}</strong></div>
                                                            <div><span>{t('vehiculef.epave')}</span><strong>{detailMoney(values.vehicule.epave)}</strong></div>
                                                            <div><span>{t('vehiculef.devis')}</span><strong>{detailMoney(values.vehicule.devis)}</strong></div>
                                                        </div>
                                                    </section>

                                                    <section className="evaluation-detail-section">
                                                        <h6><i className="ri-building-4-line"></i>{t('vehiculef.section_admin')}</h6>
                                                        <div className="evaluation-detail-grid">
                                                            <div><span>{t('vehiculef.usage')}</span><strong>{detailValue(values.vehicule.usage)}</strong></div>
                                                            <div><span>{t('vehiculef.ville')}</span><strong>{detailValue(values.vehicule.ville?.description)}</strong></div>
                                                            <div><span>{t('vehiculef.assurance')}</span><strong>{detailValue(values.vehicule.assurance?.description)}</strong></div>
                                                            <div><span>{t('vehiculef.entretReg')}</span><strong>{detailValue(values.vehicule.entretReg)}</strong></div>
                                                            <div><span>{t('vehiculef.prestataire')}</span><strong>{detailValue(values.vehicule.prestataire?.prestataire)}</strong></div>
                                                            <div><span>{t('parametrage.idFiscal')}</span><strong>{detailValue(values.vehicule.prestataire?.identFiscale)}</strong></div>
                                                        </div>
                                                    </section>

                                                    <section className="evaluation-detail-section finance">
                                                        <h6><i className="ri-money-dollar-circle-line"></i>{t('vehiculef.section_valeurs')}</h6>
                                                        <div className="evaluation-detail-grid">
                                                            <div><span>{t('vehiculef.prixAchat')}</span><strong>{detailMoney(values.vehicule.prixAchat)}</strong></div>
                                                            <div><span>{t('vehiculef.valMarche')}</span><strong>{detailMoney(values.vehicule.valMarche)}</strong></div>
                                                            <div><span>{t('vehiculef.fraisimm')}</span><strong>{detailMoney(values.vehicule.fraisimm)}</strong></div>
                                                            <div><span>{t('vehiculef.remise')}</span><strong>{detailMoney(values.vehicule.remise)}</strong></div>
                                                            <div className="wide"><span>{t('vehiculef.coefCommercial')}</span><strong>{detailValue(values.vehicule.coefCommercial, ' %')}</strong></div>
                                                        </div>
                                                    </section>
                                                </div>
                                            </CardBody>
                                        </Card>
                                    )}
                                    {values.vehicule?.documents?.length > 0 && (
                                        <Card className="mt-3 evaluation-documents-card">
                                            <CardHeader className="evaluation-documents-header">
                                                <div className="evaluation-documents-title">
                                                    <span className="evaluation-documents-icon">
                                                        <i className="ri-folder-2-line"></i>
                                                    </span>
                                                    <div>
                                                        <strong>{t('vehiculef.documents')}</strong>
                                                        <small>{values.vehicule.documents.length} document(s) associé(s)</small>
                                                    </div>
                                                </div>
                                                <Button
                                                    type="button"
                                                    color="light"
                                                    size="sm"
                                                    className="evaluation-documents-toggle"
                                                    onClick={() => setDocumentsExpanded(value => !value)}
                                                    aria-expanded={documentsExpanded}
                                                    aria-label={documentsExpanded ? "Réduire les documents" : "Afficher les documents"}
                                                >
                                                    <i className={documentsExpanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
                                                    <span>{documentsExpanded ? "Réduire" : "Afficher"}</span>
                                                </Button>
                                            </CardHeader>

                                            <Collapse isOpen={documentsExpanded}>
                                                <CardBody className="evaluation-documents-body">
                                                    <div className="evaluation-documents-scroll">
                                                        {values.vehicule.documents.map((doc, index) => {
                                                            const isPdf = doc.fileType === 'application/pdf' || doc.filenameUser?.toLowerCase().endsWith('.pdf');
                                                            return (
                                                                <div className="evaluation-document-item" key={doc.id || `${doc.filenameUser}-${index}`}>
                                                                    <span className={`evaluation-document-type ${isPdf ? "pdf" : "image"}`}>
                                                                        <i className={isPdf ? "ri-file-pdf-line" : "ri-image-line"}></i>
                                                                    </span>
                                                                    <div className="evaluation-document-copy">
                                                                        <strong title={doc.filenameUser}>{doc.filenameUser || "Document sans nom"}</strong>
                                                                        <small>{doc.createdDate || (isPdf ? "Document PDF" : "Image")}</small>
                                                                    </div>
                                                                    <Button
                                                                        type="button"
                                                                        size="sm"
                                                                        color="light"
                                                                        className="evaluation-document-view"
                                                                        onClick={() => window.open(doc.path, "_blank", "noopener,noreferrer")}
                                                                        title={t('actions.view')}
                                                                        aria-label={`Voir ${doc.filenameUser || "le document"}`}
                                                                    >
                                                                        <i className="ri-eye-line"></i>
                                                                        <span>{t('actions.view')}</span>
                                                                    </Button>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </CardBody>
                                            </Collapse>
                                        </Card>
                                    )}

                                    <Col md="12" className="d-flex justify-content-end">
                                        <Button color="primary" size="sm" className='mx-1' type="submit" disabled={!values.vehicule}>{t('actions.calculer')}</Button>
                                        <Button color="primary" size="sm" className='mx-1' type="button" onClick={handleExport} disabled={!values.vehicule}>{t('actions.exporter')}</Button>
                                        <Button color="danger" size="sm" type='reset' className="mx-2" onClick={() => setFormData({ ...initForm })}>{t('actions.clear')}</Button>
                                    </Col>
                                </CardBody>
                            </Card>
                        </AvForm>
                    </Col>
                    <Col md="4">
                        <ResultatForm formData={formData} result={result} />
                    </Col>
                </Row>
            )
            }

            <Modal
                isOpen={Boolean(evaluationError)}
                toggle={() => setEvaluationError('')}
                centered
                className="evaluation-error-modal"
            >
                <ModalHeader toggle={() => setEvaluationError('')}>
                    <span className="evaluation-error-modal-title">
                        <i className="ri-error-warning-line"></i>
                        {errorModalTitle}
                    </span>
                </ModalHeader>
                <ModalBody>
                    <p className="mb-0">{evaluationError}</p>
                </ModalBody>
                <ModalFooter>
                    {values.vehicule && selectedVehicleMissingFields.length > 0 && (
                        <Button type="button" color="warning" onClick={openQuickEdit}>
                            <i className="ri-pencil-line me-1"></i>
                            Compléter le véhicule
                        </Button>
                    )}
                    <Button type="button" color="primary" onClick={() => setEvaluationError('')}>
                        Compris
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal
                isOpen={quickEditOpen}
                toggle={closeQuickEdit}
                size="lg"
                centered
                backdrop="static"
                className="evaluation-quick-edit-modal"
            >
                <ModalHeader toggle={closeQuickEdit}>
                    <span className="evaluation-quick-edit-title">
                        <i className="ri-pencil-line"></i>
                        Compléter les données du véhicule
                    </span>
                </ModalHeader>
                <ModalBody>
                    <div className="evaluation-quick-edit-context">
                        <span><i className="ri-car-line"></i></span>
                        <div>
                            <strong>{[quickEditVehicle?.marque, quickEditVehicle?.modele].filter(Boolean).join(' ') || 'Véhicule sélectionné'}</strong>
                            <small>{quickEditVehicle?.immatriculation || quickEditVehicle?.numChassis || `Dossier #${quickEditVehicle?.id}`}</small>
                        </div>
                        <em>Les autres données du dossier seront conservées.</em>
                    </div>

                    {quickEditError && (
                        <div className="evaluation-quick-edit-error">
                            <i className="ri-error-warning-line"></i>
                            <span>{quickEditError}</span>
                        </div>
                    )}

                    <div className="evaluation-quick-edit-grid">
                        <FormGroup className="evaluation-quick-edit-field">
                            <Label for="quick-categVeh">Catégorie du véhicule <span>*</span></Label>
                            <Input
                                id="quick-categVeh"
                                type="select"
                                value={quickEditVehicle?.categVeh || ''}
                                onChange={(event) => updateQuickEditField('categVeh', event.target.value)}
                            >
                                <option value="">Sélectionner...</option>
                                {categVH.map(option => (
                                    <option key={option.id || option.code} value={option.code}>{option.description}</option>
                                ))}
                            </Input>
                        </FormGroup>
                        <FormGroup className="evaluation-quick-edit-field">
                            <Label for="quick-carburant">Carburant <span>*</span></Label>
                            <Input
                                id="quick-carburant"
                                type="select"
                                value={quickEditVehicle?.carburant || ''}
                                onChange={(event) => updateQuickEditField('carburant', event.target.value)}
                            >
                                <option value="">Sélectionner...</option>
                                <option value="Diesel">Diesel</option>
                                <option value="Essence">Essence</option>
                                <option value="Hybrid">Hybride</option>
                            </Input>
                        </FormGroup>
                        <FormGroup className="evaluation-quick-edit-field">
                            <Label for="quick-etatVehicule">État du véhicule <span>*</span></Label>
                            <Input
                                id="quick-etatVehicule"
                                type="select"
                                value={quickEditVehicle?.etatVehicule || ''}
                                onChange={(event) => updateQuickEditField('etatVehicule', event.target.value)}
                            >
                                <option value="">Sélectionner...</option>
                                <option value="ETAT_1">Bon</option>
                                <option value="ETAT_2">Moyen</option>
                            </Input>
                        </FormGroup>
                        {[
                            ['puissFiscale', 'Puissance fiscale', 'CV', 1],
                            ['prixAchat', "Prix d'achat", 'MAD', 0],
                            ['compteur', 'Kilométrage', 'km', 0],
                            ['ageYears', 'Âge en années', 'ans', 0],
                            ['ageMonths', 'Âge en mois', 'mois', 0],
                            ['epave', 'Valeur épave', 'MAD', 0],
                            ['devis', 'Montant du devis', 'MAD', 0],
                            ['valMarche', 'Valeur de marché', 'MAD', 0],
                        ].map(([field, label, suffix, min]) => (
                            <FormGroup key={field} className="evaluation-quick-edit-field">
                                <Label for={`quick-${field}`}>{label} <span>*</span></Label>
                                <div className="evaluation-quick-edit-input">
                                    <Input
                                        id={`quick-${field}`}
                                        type="number"
                                        min={min}
                                        step={field === 'ageYears' || field === 'ageMonths' || field === 'puissFiscale' ? 1 : 0.01}
                                        value={quickEditVehicle?.[field] ?? ''}
                                        onChange={(event) => updateQuickEditField(field, event.target.value)}
                                    />
                                    <small>{suffix}</small>
                                </div>
                            </FormGroup>
                        ))}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <div className="evaluation-quick-edit-footer-note">
                        <i className="ri-information-line"></i>
                        Après l’enregistrement, vous pourrez relancer immédiatement l’évaluation.
                    </div>
                    <Button type="button" color="light" onClick={closeQuickEdit} disabled={isFetching}>
                        Annuler
                    </Button>
                    <Button type="button" color="primary" onClick={saveQuickVehicle} disabled={isFetching}>
                        {isFetching ? (
                            <><i className="ri-loader-4-line spin-icon me-1"></i>Enregistrement...</>
                        ) : (
                            <><i className="ri-save-line me-1"></i>Enregistrer et continuer</>
                        )}
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal
                size="xl"
                isOpen={show}
                toggle={showVehiculePopup} >
                <ModalHeader toggle={showVehiculePopup}>
                    {t('vehicule')}

                </ModalHeader>
                <ModalBody>
                    <div className="table-responsive">
                        <Table striped className=" mb-0">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>{t('vehiculef.marque')}</th>
                                    <th>{t('vehiculef.modele')}</th>
                                    <th>{t('vehiculef.carburant')}</th>
                                    <th>{t('vehiculef.nbrCylindres')}</th>
                                    <th>{t('vehiculef.puissFiscale')}</th>
                                    <th>{t('vehiculef.prixAchat')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    vehicules
                                        .map(e => {
                                            return (
                                                <tr key={e.id}>
                                                    <td>
                                                        <React.Fragment>
                                                            <div className="form-check mb-3">
                                                                <Input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    onChange={(event) => handleVehicule(event.target.checked, e)}
                                                                    checked={values.vehicule && values.vehicule.id === e.id}
                                                                />
                                                            </div>
                                                        </React.Fragment>
                                                    </td>
                                                    <td>{e.marque}</td>
                                                    <td>{e.modele}</td>
                                                    <td>{e.carburant}</td>
                                                    <td>{e.nbrCylindres}</td>
                                                    <td>{e.puissFiscale}</td>
                                                    <td>{e.prixAchat}</td>
                                                </tr>
                                            );
                                        })
                                }
                            </tbody>
                        </Table>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button
                        type="button"
                        size='sm'
                        onClick={showVehiculePopup}
                        color="primary"
                        className="waves-effect"
                    >
                        {t('actions.close')}
                    </Button>

                </ModalFooter>
            </Modal>


        </React.Fragment >
    );
};

export default VVinalForm;
